import { Building2, Users, Loader2, Wrench, Plus, ArrowUpRight, Clock, ShieldCheck, TrendingUp, AlertCircle, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/useAppContext';
import { supabase } from '../lib/supabase';
import { formatCurrency, formatDate } from '../utils/formatters';
import './DashboardHome.css';

// ── Inline SVG area chart — real payment data ────────────────────────────
function RevenueChart({ buckets }: { buckets: { label: string; value: number }[] }) {
    const W = 520;
    const H = 88;
    const raw = buckets.map(b => b.value);
    const max = Math.max(...raw, 1);
    const pts = raw.map((v, i) => ({
        x: raw.length === 1 ? W / 2 : (i / (raw.length - 1)) * W,
        y: H - (v / max) * H * 0.88 + H * 0.06,
    }));
    const line = pts.map((p, i) =>
        i === 0
            ? `M${p.x},${p.y}`
            : `C${pts[i - 1].x + (p.x - pts[i - 1].x) * 0.55},${pts[i - 1].y} ${p.x - (p.x - pts[i - 1].x) * 0.55},${p.y} ${p.x},${p.y}`
    ).join('');
    const area = line + ` L${W},${H} L0,${H}Z`;

    return (
        <div className="rev-chart">
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="rev-chart__svg">
                <defs>
                    <linearGradient id="rev-grad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#16a34a" stopOpacity="0.28" />
                        <stop offset="100%" stopColor="#16a34a" stopOpacity="0.02" />
                    </linearGradient>
                </defs>
                <path d={area} fill="url(#rev-grad)" />
                <path d={line} fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                {pts.map((p, i) => (
                    <circle key={i} cx={p.x} cy={p.y} r={i === pts.length - 1 ? 5 : 3}
                        fill={i === pts.length - 1 ? '#16a34a' : '#4ade80'}
                        opacity={i === pts.length - 1 ? 1 : 0.55}
                    />
                ))}
            </svg>
            <div className="rev-chart__labels">
                {buckets.map(b => <span key={b.label}>{b.label}</span>)}
            </div>
        </div>
    );
}

/** Build a 6-month bucket array ending at the current month, with correct labels. */
function buildBuckets(payments: any[]): { label: string; value: number }[] {
    const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const now = new Date();
    const buckets = Array.from({ length: 6 }, (_, i) => {
        const d = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        return { label: MONTHS[d.getMonth()], key, value: 0 };
    });
    payments.forEach(p => {
        if (p.status !== 'paid' || !p.paid_date) return;
        const paidKey = p.paid_date.slice(0, 7); // "YYYY-MM"
        const bucket = buckets.find(b => b.key === paidKey);
        if (bucket) bucket.value += Number(p.amount) || 0;
    });
    return buckets;
}

export default function DashboardHome() {
    const { user } = useAppContext();
    const navigate = useNavigate();
    const [properties, setProperties] = useState<any[]>([]);
    const [payments, setPayments] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        tenants: 0,
        openTickets: 0,
        recentActivity: [] as any[]
    });

    const fetchDashboardData = async () => {
        if (!user.id) return;
        try {
            setLoading(true);
            const { data: props, error: propsError } = await supabase
                .from('properties')
                .select('*')
                .eq('owner_id', user.id)
                .order('created_at', { ascending: false });

            if (propsError) console.error('Error fetching props:', propsError);
            const propertyList = props || [];
            setProperties(propertyList);
            const propertyIds = propertyList.map(p => p.id);

            if (propertyIds.length > 0) {
                const [
                    { count: tenantCount },
                    { data: tix, error: tixError },
                    { data: payData },
                ] = await Promise.all([
                    supabase.from('tenant_links').select('*', { count: 'exact', head: true }).in('property_id', propertyIds),
                    supabase.from('maintenance_tickets').select('*').in('property_id', propertyIds).order('created_at', { ascending: false }),
                    supabase.from('rent_payments').select('id,amount,status,paid_date,period_month').in('property_id', propertyIds),
                ]);

                setPayments(payData || []);

                if (tixError) console.error('Error fetching tickets:', tixError);

                const activity = [
                    ...(tix || []).slice(0, 5).map(t => ({
                        id: t.id,
                        type: 'maintenance',
                        title: t.title,
                        date: t.created_at,
                        status: t.status
                    }))
                ];

                setStats({
                    tenants: tenantCount || 0,
                    openTickets: (tix || []).filter(t => t.status !== 'resolved').length,
                    recentActivity: activity
                });
            } else {
                setStats({ tenants: 0, openTickets: 0, recentActivity: [] });
            }
        } catch (err) {
            console.error('Unexpected error in dashboard:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, [user.id]);

    const totalMonthlyRent = properties.reduce((acc, p) => acc + (p.rent_amount || 0), 0);
    const revBuckets = buildBuckets(payments);
    const thisMonthCollected = revBuckets[revBuckets.length - 1]?.value ?? 0;
    const prevMonthCollected = revBuckets[revBuckets.length - 2]?.value ?? 0;
    const revTrend = prevMonthCollected === 0
        ? null
        : Math.round(((thisMonthCollected - prevMonthCollected) / prevMonthCollected) * 100);

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 100px)' }}>
                <Loader2 className="animate-spin" size={48} color="var(--color-accent)" />
            </div>
        );
    }

    return (
        <div className="page dashboard-page">
            <div className="page__header dashboard-page-header">
                <div className="dashboard-page-heading">
                    <h1 className="page__title dh-title">Welcome, {user.name || 'Landlord'}</h1>
                    <p className="page__subtitle" style={{ fontSize: '1.0625rem', fontWeight: 500, color: 'var(--color-text-secondary)' }}>
                        {stats.openTickets > 0
                            ? `You have ${stats.openTickets} active maintenance request${stats.openTickets > 1 ? 's' : ''} to review`
                            : 'Everything looks clear — no pending maintenance.'}
                    </p>
                </div>
                <div className="dashboard-page-actions">
                    <button className="btn btn-secondary dh-btn-portfolio" onClick={() => navigate('/app/landlord/properties')}>
                        View Portfolio
                    </button>
                    <button className="btn btn-primary dh-btn-add" onClick={() => navigate('/app/landlord/properties')}>
                        <Plus size={18} /> Add Property
                    </button>
                </div>
            </div>

            {/* Free plan upgrade nudge */}
            {user.plan === 'Free' && (
                <div className="dh-upgrade-nudge">
                    <div className="dh-upgrade-nudge__left">
                        <div className="dh-upgrade-nudge__icon">
                            <Zap size={20} color="#fff" />
                        </div>
                        <div>
                            <div className="dh-upgrade-nudge__title">You're on the Free plan</div>
                            <div className="dh-upgrade-nudge__sub">Upgrade to Pro for unlimited properties, more tenants, documents and priority support.</div>
                        </div>
                    </div>
                    <button onClick={() => navigate('/app/landlord/settings')} className="dh-upgrade-nudge__btn">
                        Upgrade to Pro →
                    </button>
                </div>
            )}

            {/* KPI Cards */}
            <div className="responsive-grid-4 dashboard-kpi-grid dh-kpi-grid">
                {[
                    { label: 'Total Properties', value: properties.length, icon: Building2, color: '#16a34a', bg: 'rgba(22,163,74,0.09)', tag: 'Portfolio' },
                    { label: 'Managed Tenants', value: stats.tenants, icon: Users, color: '#2563eb', bg: 'rgba(37,99,235,0.09)', tag: 'Active' },
                    { label: 'Expected Revenue', value: formatCurrency(totalMonthlyRent), icon: TrendingUp, color: '#16a34a', bg: 'rgba(22,163,74,0.09)', tag: 'Monthly' },
                    {
                        label: 'Open Tickets', value: stats.openTickets, icon: Wrench,
                        color: stats.openTickets > 0 ? '#ef4444' : '#16a34a',
                        bg: stats.openTickets > 0 ? 'rgba(239,68,68,0.09)' : 'rgba(22,163,74,0.09)',
                        tag: stats.openTickets > 0 ? 'Urgent' : 'Clear'
                    }
                ].map((kpi, i) => (
                    <div key={i} className="dh-kpi-card">
                        <div className="dh-kpi-card__top">
                            <div className="dh-kpi-card__icon" style={{ background: kpi.bg, color: kpi.color }}>
                                <kpi.icon size={24} />
                            </div>
                            <span className="dh-kpi-card__tag" style={{ color: kpi.color, background: kpi.bg }}>
                                {kpi.tag}
                            </span>
                        </div>
                        <div className="dh-kpi-card__value">{kpi.value}</div>
                        <div className="dh-kpi-card__label">{kpi.label}</div>
                        <div className="dh-kpi-card__glow" style={{ background: kpi.bg }} />
                    </div>
                ))}
            </div>

            {/* Revenue Pulse chart */}
            <div className="dh-revenue-panel">
                <div className="dh-revenue-panel__header">
                    <div>
                        <div className="dh-revenue-panel__eyebrow">Revenue Pulse</div>
                        <div className="dh-revenue-panel__amount">{formatCurrency(thisMonthCollected)}</div>
                        <div className="dh-revenue-panel__sub">collected this month · {properties.length} {properties.length === 1 ? 'property' : 'properties'}</div>
                    </div>
                    <div className="dh-revenue-panel__badge" style={{
                        background: revTrend === null ? 'rgba(100,116,139,0.15)' : revTrend >= 0 ? 'rgba(22,163,74,0.15)' : 'rgba(220,38,38,0.15)',
                        color: revTrend === null ? '#64748b' : revTrend >= 0 ? '#16a34a' : '#dc2626',
                    }}>
                        <TrendingUp size={14} />
                        {revTrend === null ? 'No prior data' : revTrend === 0 ? 'Flat vs last month' : `${revTrend > 0 ? '+' : ''}${revTrend}% vs last month`}
                    </div>
                </div>
                <RevenueChart buckets={revBuckets} />

                {/* Per-property mini bars */}
                {properties.length > 0 && (
                    <div className="dh-revenue-breakdown">
                        {properties.slice(0, 5).map(p => {
                            const pct = totalMonthlyRent > 0 ? Math.round((p.rent_amount / totalMonthlyRent) * 100) : 0;
                            return (
                                <div key={p.id} className="dh-rev-row">
                                    <span className="dh-rev-row__name">{p.name}</span>
                                    <div className="dh-rev-row__bar-wrap">
                                        <div className="dh-rev-row__bar" style={{ width: `${pct}%` }} />
                                    </div>
                                    <span className="dh-rev-row__val">{formatCurrency(p.rent_amount)}</span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="dashboard-grid-2">
                {/* Portfolio Preview */}
                <div className="dh-panel">
                    <div className="dh-panel__header">
                        <h2 className="dh-panel__title">Portfolio Preview</h2>
                        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/app/landlord/properties')} style={{ borderRadius: '10px', fontWeight: 600 }}>See All</button>
                    </div>
                    <div className="dh-panel__body">
                        {properties.length === 0 ? (
                            <div className="dh-empty">
                                <div className="dh-empty__icon">
                                    <Building2 size={36} color="var(--color-text-tertiary)" opacity={0.5} />
                                </div>
                                <h3 className="dh-empty__title">No properties yet</h3>
                                <p className="dh-empty__sub">Start building your digital portfolio today.</p>
                                <button className="btn btn-primary" onClick={() => navigate('/app/landlord/properties')} style={{ borderRadius: '10px', marginTop: '1rem' }}>Add First Property</button>
                            </div>
                        ) : (
                            <div className="dashboard-card-grid-2">
                                {properties.slice(0, 4).map(prop => (
                                    <div
                                        key={prop.id}
                                        className="dh-prop-card"
                                        onClick={() => navigate(`/app/landlord/properties/${prop.id}`)}
                                    >
                                        <div className="dh-prop-card__name">{prop.name}</div>
                                        <div className="dh-prop-card__addr">{prop.address || 'Location not set'}</div>
                                        <div className="dh-prop-card__foot">
                                            <span className="dh-prop-card__rent">{formatCurrency(prop.rent_amount)}</span>
                                            <span className="dh-prop-card__day">DAY {prop.due_day}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Recent Activity */}
                <div className="dh-panel">
                    <div className="dh-panel__header">
                        <h2 className="dh-panel__title">Recent Activity</h2>
                    </div>
                    <div className="dh-panel__body">
                        {stats.recentActivity.length === 0 ? (
                            <div className="dh-empty">
                                <Clock size={40} color="var(--color-text-tertiary)" style={{ opacity: 0.3, marginBottom: '1rem' }} />
                                <p style={{ color: 'var(--color-text-tertiary)', fontSize: '0.9375rem', fontWeight: 500 }}>No recent activity to display.</p>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {stats.recentActivity.map((act) => (
                                    <div key={act.id} className="dh-activity-item">
                                        <div className="dh-activity-item__icon" style={{
                                            background: act.status === 'resolved' ? 'rgba(16,185,129,0.09)' : act.status === 'in_progress' ? 'rgba(245,158,11,0.09)' : 'rgba(239,68,68,0.09)',
                                        }}>
                                            {act.status === 'resolved'
                                                ? <ShieldCheck size={20} color="#10b981" />
                                                : act.status === 'in_progress'
                                                    ? <Wrench size={20} color="#d97706" />
                                                    : <AlertCircle size={20} color="#ef4444" />}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div className="dh-activity-item__title">{act.title}</div>
                                            <div className="dh-activity-item__meta">
                                                <Clock size={12} /> {formatDate(act.date)} ·{' '}
                                                <span style={{
                                                    color: act.status === 'resolved' ? '#10b981' : act.status === 'in_progress' ? '#d97706' : '#ef4444',
                                                    textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em'
                                                }}>{act.status.replace('_', ' ')}</span>
                                            </div>
                                        </div>
                                        <ArrowUpRight size={16} color="var(--color-text-tertiary)" />
                                    </div>
                                ))}
                                <button className="btn btn-secondary" onClick={() => navigate('/app/landlord/maintenance')}
                                    style={{ marginTop: '0.5rem', width: '100%', borderRadius: '12px', padding: '0.75rem', fontWeight: 700, fontSize: '0.9375rem' }}>
                                    View Full History
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
